import { ComponentPropsWithRef, ReactElement, ReactNode, useMemo, useRef } from "react";
import { Box, Button } from "@mui/material";
import { Add } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

/** A button in a picker's list that opens a form to create the record the
 *  user could not find (e.g. "Add new client"). Null when there is none - most
 *  often because the user lacks the matching add permission. */
export type AddNewOption = { label: string; onClick: () => void } | null | undefined;

/**
 * What a picker needs to offer "Add new …": a list component that puts the
 * button at the top of the options, and a "No options" text with the button
 * under it - MUI shows that text instead of the list when nothing matches,
 * which is exactly when the user needs the button. Both are undefined without
 * `addNew`, so the picker falls back to its defaults.
 */
export function useAddNewOption(addNew: AddNewOption): {
  listboxComponent?: (props: ComponentPropsWithRef<"ul">) => ReactElement;
  noOptionsText?: ReactNode;
} {
  const { t } = useTranslation();
  // Read at click time, so callers needn't memoise the handler and the list
  // isn't remounted (losing its scroll position) on every render.
  const onClickRef = useRef<() => void>(() => {});
  onClickRef.current = addNew?.onClick ?? (() => {});

  const label = addNew?.label;

  const button = useMemo(
    () =>
      label ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2, px: 1 }}>
          <Button
            variant="contained"
            size="small"
            startIcon={<Add />}
            onClick={() => onClickRef.current()}
          >
            {label}
          </Button>
        </Box>
      ) : null,
    [label]
  );

  const listboxComponent = useMemo(
    () =>
      button
        ? function AddNewListbox({ children, ...listboxProps }: ComponentPropsWithRef<"ul">) {
            return (
              <ul {...listboxProps}>
                <Box component="li" sx={{ listStyle: "none" }}>
                  {button}
                </Box>
                {children}
              </ul>
            );
          }
        : undefined,
    [button]
  );

  const noOptionsText = useMemo(
    () =>
      button ? (
        <>
          {t("No options")}
          {button}
        </>
      ) : undefined,
    [button, t]
  );

  return { listboxComponent, noOptionsText };
}
